import { DocumentScanResponse, ScannerInfo, AAMVAInfo, toast, visitorManagementClient, VisitorManagementBridgeStatus } from "common";
import { Accessor, createEffect, createMemo, createSignal, Setter } from "solid-js";
import { DocumentScanResult } from "../../api";

export class DocumentReaderContext {
    scanners: Accessor<ScannerInfo[]>; setScanners: Setter<ScannerInfo[]>
    scannersLoading: Accessor<boolean>; setScannersLoading: Setter<boolean>;
    selectedScannerId: Accessor<string>; setSelectedScannerId: Setter<string>;
    selectedScanner: Accessor<ScannerInfo | undefined>;

    attempted: Accessor<boolean>; setAttempted: Setter<boolean>;
    scan: Accessor<DocumentScanResult | undefined>; setScan: Setter<DocumentScanResult | undefined>;
    isReading: Accessor<boolean>; setIsReading: Setter<boolean>;
    photoBase64: Accessor<string | undefined>; setPhotoBase64: Setter<string | undefined>;
    idBase64: Accessor<string | undefined>; setIdBase64: Setter<string | undefined>;
    aamva: Accessor<AAMVAInfo | undefined>; setAamva: Setter<AAMVAInfo | undefined>;
    status: Accessor<'pending' | 'reading' | 'success' | 'error'>;
    onScan?: (value: DocumentScanResult) => void;

    vmBridgeStatus: Accessor<VisitorManagementBridgeStatus | undefined>; setVmBridgeStatus: Setter<VisitorManagementBridgeStatus | undefined>;


    installRequired: Accessor<boolean>; setInstallRequired: Setter<boolean>;
    updateRequired: Accessor<boolean>; setUpdateRequired: Setter<boolean>;

    constructor(scan?: DocumentScanResult, onScan?: (value: DocumentScanResult) => void) {
        [this.scanners, this.setScanners] = createSignal<ScannerInfo[]>([]);
        [this.scannersLoading, this.setScannersLoading] = createSignal(false);
        [this.selectedScannerId, this.setSelectedScannerId] = createSignal<string>('');
        [this.attempted, this.setAttempted] = createSignal(false);
        [this.scan, this.setScan] = createSignal<DocumentScanResult | undefined>();
        [this.isReading, this.setIsReading] = createSignal(false);
        [this.photoBase64, this.setPhotoBase64] = createSignal<string | undefined>(scan?.photoBase64);
        [this.idBase64, this.setIdBase64] = createSignal<string | undefined>(scan?.idBase64);
        [this.aamva, this.setAamva] = createSignal<AAMVAInfo | undefined>(scan?.AAMVA);
        [this.updateRequired, this.setUpdateRequired] = createSignal<boolean>(false);
        [this.installRequired, this.setInstallRequired] = createSignal<boolean>(false);
        [this.vmBridgeStatus, this.setVmBridgeStatus] = createSignal<VisitorManagementBridgeStatus>();

        this.onScan = onScan;

        this.selectedScanner = createMemo(() => {
            return this.scanners().find(s => s.id === this.selectedScannerId());
        });

        this.status = createMemo(() => {
            if (this.isReading())
                return 'reading';
            if (this.photoBase64())
                return 'success';
            if (!this.attempted())
                return 'pending';
            return 'error';
        });

        this.refreshScanners();
    }

    async refreshScanners() {
        this.setScannersLoading(true);
        try {
            const scanners = await visitorManagementClient.listScanners();
            this.setScanners(scanners);

            if (!this.selectedScanner() && scanners.length > 0) {
                this.selectScanner(scanners[0]);
            }
        } catch (e) {
            console.error('Failed to refresh scanners:', e);
            toast.notify('error', 'Failed to refresh scanners. Please try again.');
        } finally {
            this.setScannersLoading(false);
        }
    }

    selectScanner(scanner: ScannerInfo) {
        this.setSelectedScannerId(scanner.id);
    }

    async read() {
        const scanner = this.selectedScanner();
        if (!scanner)
            return;

        this.setIsReading(true);
        try {
            const scan = await visitorManagementClient.scanDocument(scanner.type, scanner.id);
            const result: DocumentScanResult = {
                AAMVA: scan.aamvaInfo,
                idBase64: scan.front,
                photoBase64: scan.photo,
            }

            this.onScan?.(result);
            this.setScan(result);
            this.setAamva(result.AAMVA);
            this.setIdBase64(result.idBase64);
            this.setPhotoBase64(result.photoBase64);
        } catch (e) {
            if (e instanceof Error) {
                console.error(e);
            } else {
                console.error(`Error reading document ${e}`);
            }
        }
        this.setIsReading(false);
    }

    async init() {
        const status = await visitorManagementClient.getStatus();
        this.setVmBridgeStatus(status);
    }
}
